/**
 * Secure Auth shim for TopoloRoadmapper
 *
 * The app now consolidates onto @topolo-io/app-shell's canonical auth client
 * (see lib/topolo-auth.ts). This module is kept as a thin compatibility surface
 * so the remaining consumers (api-keys / comments / attachments sections, the
 * api/auth helpers) keep importing the SAME token store and helpers — there is
 * now a single underlying browser session controller, not a parallel one.
 */

import type { PasswordLoginRequest } from '@topolo-io/auth-client';
import type { AppUser, AppAuthTokens } from '@topolo-io/app-shell';
import {
  authClient as topoloAuthClient,
  accessTokenMemory as memoryStore,
  accessTokenSession as sessionStore,
  buildUserFromAccessToken as buildUser,
  decodeJwtPayload as decodeJwt,
  getTokenExpiration as getExpiration,
  isTokenExpired as tokenExpired,
  isTokenFullyExpired as tokenFullyExpired,
} from './topolo-auth';

export type AuthUser = AppUser;
export type AuthTokens = AppAuthTokens;

export const accessTokenMemory = memoryStore;
export const accessTokenSession = sessionStore;

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  return decodeJwt(token);
}

export function buildUserFromAccessToken(token: string): AuthUser | null {
  return buildUser(token);
}

export function getTokenExpiration(token: string): number | null {
  return getExpiration(token);
}

export function isTokenExpired(token: string): boolean {
  return tokenExpired(token);
}

export function isTokenFullyExpired(token: string): boolean {
  return tokenFullyExpired(token);
}

/**
 * Compatibility wrapper preserving the prior SecureAuthClient method surface,
 * delegating to the canonical app-shell auth client.
 */
export class SecureAuthClient {
  async checkExistingSession() {
    return topoloAuthClient.checkExistingSession();
  }

  async login(provider?: string, targetPath?: string): Promise<{ authenticated: boolean; tokens?: AuthTokens; user?: AuthUser }> {
    // app-shell's login owns the SSO callback target; targetPath is retained for
    // signature compatibility but no longer threaded through the redirect.
    void targetPath;
    return topoloAuthClient.login(provider);
  }

  async loginWithPassword(credentials: PasswordLoginRequest) {
    return topoloAuthClient.loginWithPassword(credentials);
  }

  async refreshSession(options: { allowOnLoginRoute?: boolean } = {}) {
    return topoloAuthClient.refreshSession(options);
  }

  async fetchCurrentUser(accessToken?: string | null): Promise<AuthUser | null> {
    return topoloAuthClient.fetchCurrentUser(accessToken);
  }

  async logout(): Promise<void> {
    await topoloAuthClient.logout();
  }

  getAccessToken(): string | null {
    return topoloAuthClient.getAccessToken();
  }

  async handleAuthCallbackAsync(input?: string | URL | URLSearchParams) {
    return topoloAuthClient.handleAuthCallbackAsync(input);
  }

  decodeToken(token: string): AuthUser | null {
    return buildUser(token);
  }

  isTokenExpired(token: string): boolean {
    return tokenExpired(token);
  }
}

export const secureAuthClient = new SecureAuthClient();

// Legacy token-storage compatibility surface.
export const tokenStorage = {
  getTokens(): AuthTokens | null {
    const accessToken = accessTokenMemory.get() || accessTokenSession.get();
    if (!accessToken) return null;
    return { accessToken, refreshToken: '' };
  },

  setTokens(tokens: AuthTokens): void {
    accessTokenMemory.set(tokens.accessToken);
    accessTokenSession.set(tokens.accessToken);
  },

  clearTokens(): void {
    accessTokenMemory.clear();
    accessTokenSession.clear();
  },

  getUser(): AuthUser | null {
    return null;
  },

  setUser(_user: AuthUser): void {},
  clearUser(): void {},
};

// Also export as authClient for compatibility.
export const authClient = secureAuthClient;
